import {
    SimpleAndFilter,
    SimpleOrFilter,
} from "@impero-com/common/utils/indexing";

import {
    DayOfWeek,
    ControlsPageFiltersForControlsWeeklyPageSpecificFilters as ControlsWeeklyFilters,
    ControlsWeeklyPageSpecificFilters,
    ControlsWeeklyColumn,
} from "api/generated/model";
import { PageStateData } from "org/pageState/models";
import { PageFilterAndColumns } from "org/pageState/types";
import { unwrapPageStateAs } from "org/pageState/utils";

import { decodeOrFilter, encodeOrFilter } from "../common";

import {
    createDecodePageFiltersFn,
    createEncodeColumnFn,
    createEncodePageFiltersFn,
} from "./common";

type AllColumns = { [K in ControlsWeeklyColumn]: null };
const allColumnsGuard: AllColumns = {
    name: null,
    description: null,
    program: null,
    status: null,
    recurrenceWeekly: null,
    notification: null,
    assignment: null,
    loginProtection: null,
    segregationOfDuties: null,
    tags: null,
    accessRight: null,
    controlResultCount: null,
    nextDueDate: null,
};
const ALL_COLUMNS: ControlsWeeklyColumn[] = Object.keys(
    allColumnsGuard
) as ControlsWeeklyColumn[];

const encodeControlsWeeklyColumn: (col: string) => ControlsWeeklyColumn =
    createEncodeColumnFn(ALL_COLUMNS);

// Filters
//
function encodeControlsWeeklySpecificPageFilters(
    filters: Array<SimpleOrFilter>
): ControlsWeeklyPageSpecificFilters {
    return {
        notification: encodeOrFilter<number>("notification", filters),
        onlySearchRecurrenceWeeklyDays: encodeOrFilter<DayOfWeek[]>(
            "onlySearchRecurrenceWeeklyDays",
            filters
        ),
        onlySearchRecurrenceWeeklyPeriodicity: encodeOrFilter<number>(
            "onlySearchRecurrenceWeeklyPeriodicity",
            filters
        ),
    };
}
const encodeControlsWeeklyPageFilters: (
    simpleAndFilter: SimpleAndFilter | null
) => ControlsWeeklyFilters = createEncodePageFiltersFn(
    encodeControlsWeeklySpecificPageFilters
);

function decodeControlsWeeklyPageSpecificFilters(
    specificFilters: ControlsWeeklyPageSpecificFilters
): Array<SimpleOrFilter> {
    const output: Array<SimpleOrFilter> = [];
    if (specificFilters.notification) {
        output.push(
            decodeOrFilter<number>("notification", specificFilters.notification)
        );
    }
    if (specificFilters.onlySearchRecurrenceWeeklyDays) {
        output.push(
            decodeOrFilter<DayOfWeek[]>(
                "onlySearchRecurrenceWeeklyDays",
                specificFilters.onlySearchRecurrenceWeeklyDays
            )
        );
    }
    if (specificFilters.onlySearchRecurrenceWeeklyPeriodicity) {
        output.push(
            decodeOrFilter<number>(
                "onlySearchRecurrenceWeeklyPeriodicity",
                specificFilters.onlySearchRecurrenceWeeklyPeriodicity
            )
        );
    }
    return output;
}
const decodeControlsWeeklyPageFilters: (
    pageFilters: ControlsWeeklyFilters
) => Array<SimpleOrFilter> = createDecodePageFiltersFn(
    decodeControlsWeeklyPageSpecificFilters
);

// Page state
//
type ControlsWeeklyPageStateWithType = PageStateData<"ControlsWeekly"> & {
    type: "ControlsWeekly";
};

function decodeControlsWeeklyPageState(
    pageState: ControlsWeeklyPageStateWithType
): PageFilterAndColumns {
    const weeklylPageState = unwrapPageStateAs(pageState, "ControlsWeekly");
    const filter: SimpleAndFilter = {
        filters: decodeControlsWeeklyPageFilters(weeklylPageState.filters),
    };
    const visibleColumnIds = new Set(weeklylPageState.columns);
    return {
        pageType: pageState.type,
        filter,
        visibleColumnIds,
        version: pageState.version ?? 0,
    };
}

function encodeControlsWeeklyPageState(
    filterAndColumns: PageFilterAndColumns
): ControlsWeeklyPageStateWithType {
    const { filter, visibleColumnIds } = filterAndColumns;

    return {
        type: "ControlsWeekly",
        filters: encodeControlsWeeklyPageFilters(filter),
        columns: Array.from(visibleColumnIds).map(encodeControlsWeeklyColumn),
        version: filterAndColumns.version,
    };
}

export { encodeControlsWeeklyPageState, decodeControlsWeeklyPageState };

export type { ControlsWeeklyPageStateWithType };
